import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Apple, Play, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { preSignup, api } from "@/lib/api";
import { toast } from "sonner";

const KEY_DONE = "chronos.presignup.v1";

export default function PreSignupSection() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("any");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    try { if (localStorage.getItem(KEY_DONE)) setDone(true); } catch (e) {}
    api.get("/pre-signup/count").then((r) => setTotal(r.data?.total || 0)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error(t("presignup.invalid"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await preSignup(trimmed, platform, i18n.language, "landing");
      setTotal(r.total);
      setDone(true);
      try { localStorage.setItem(KEY_DONE, JSON.stringify({ email: trimmed, at: Date.now() })); } catch (err) {}
      toast.success(t("presignup.success"));
    } catch (err) {
      const isInvalid = err?.response?.status === 400;
      toast.error(isInvalid ? t("presignup.invalid") : t("presignup.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 py-14" data-testid="presignup-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="rounded-3xl glass-strong p-8 sm:p-10 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/25 via-transparent to-amber-900/20" aria-hidden />
        <div className="relative z-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-bold">{t("presignup.kicker")}</div>
          <h3 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50">
            {t("presignup.title")}
          </h3>
          <p className="mt-3 text-stone-300 max-w-2xl mx-auto">{t("presignup.desc")}</p>
          {typeof total === "number" && total > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs glass rounded-full px-3 py-1 text-fuchsia-200" data-testid="presignup-count">
              <Mail className="w-3 h-3" />
              {t("presignup.count", { n: total })}
            </div>
          )}

          {done ? (
            <div className="mt-6 inline-flex items-center gap-3 glass rounded-2xl px-6 py-4 text-emerald-200" data-testid="presignup-done">
              <Check className="w-5 h-5 text-emerald-300" />
              <span className="font-bold">{t("presignup.done")}</span>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="presignup-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("presignup.email_placeholder")}
                className="flex-1 rounded-full px-5 py-3 bg-stone-950/60 border border-white/15 text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-fuchsia-400/60"
                data-testid="presignup-email"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold rounded-full px-6 py-3 h-auto"
                data-testid="presignup-submit"
              >
                <Mail className="w-4 h-4 mr-2" />
                {submitting ? t("presignup.sending") : t("presignup.cta")}
              </Button>
            </form>
          )}

          {!done && (
            <div className="mt-4 flex justify-center gap-2 flex-wrap">
              {[
                { k: "any",     l: t("presignup.platform_any"),     icon: null },
                { k: "ios",     l: t("presignup.platform_ios"),     icon: Apple },
                { k: "android", l: t("presignup.platform_android"), icon: Play },
              ].map((p) => {
                const Icon = p.icon;
                const active = platform === p.k;
                return (
                  <button
                    key={p.k}
                    type="button"
                    onClick={() => setPlatform(p.k)}
                    className={`text-xs rounded-full px-3 py-1 border transition-colors flex items-center gap-1.5 ${
                      active
                        ? "bg-fuchsia-500 text-stone-950 border-fuchsia-400"
                        : "border-white/15 text-stone-300 hover:border-fuchsia-400/50"
                    }`}
                    data-testid={`presignup-platform-${p.k}`}
                  >
                    {Icon && <Icon className="w-3 h-3" />}
                    {p.l}
                  </button>
                );
              })}
            </div>
          )}

          <p className="mt-4 text-xs text-stone-400">{t("presignup.privacy_note")}</p>
        </div>
      </motion.div>
    </section>
  );
}
